// Zebra Browser Print base URL — mirrors the SDK's own logic:
// HTTP pages → port 9100 (plain HTTP), HTTPS pages → port 9101 (HTTPS).
const BP = window.location.protocol === 'https:'
  ? 'https://localhost:9101'
  : 'http://localhost:9100';

/**
 * Returns the first detected Zebra printer via Zebra Browser Print, or null.
 * Times out after 3 s so it doesn't hang when the service isn't running.
 */
export async function getPrinter() {
  try {
    // /default?type=printer is the primary endpoint the Zebra Browser Print SDK uses for sending jobs
    const r = await fetch(`${BP}/default?type=printer`, { signal: AbortSignal.timeout(3000) });
    const d = await r.json();
    console.log('[Zebra] /default response:', d);
    if (d && d.connection !== undefined) return d;

    // Fall back to /available if no default is set
    const r2 = await fetch(`${BP}/available`, { signal: AbortSignal.timeout(3000) });
    const d2 = await r2.json();
    console.log('[Zebra] /available response:', d2);
    return d2.printer?.[0] ?? null;
  } catch (e) {
    console.error('[Zebra] getPrinter failed:', e);
    return null;
  }
}

/**
 * Sends a Code 128 barcode ZPL to the printer.
 * Label size: 76.2 mm × 50.8 mm @ 203 dpi (ZD421 default roll).
 *
 * @param {object} printer  - printer object returned by getPrinter()
 * @param {string} barcode  - barcode value to encode
 * @param {string} [label]  - optional item name printed above the barcode
 * @param {number} [qty=1]  - number of copies
 */
export async function printLabel(printer, barcode, label = '', qty = 1) {
  // Minimal ZPL — no media mode / size overrides, just content.
  // Let the printer use its own configured defaults for dimensions and media type.
  const PW = 609;  // 76.2 mm × (203 dpi ÷ 25.4 mm/in) = 609 dots
  const LL = 406;  // 50.8 mm × 8 = 406 dots
  const BAR_W = 2; // narrow bar width in dots (2 = Code 128 default)

  // Horizontally center the barcode using the Code 128 width formula:
  //   (N_data + 2 symbols) × 11 modules  +  13 stop modules  +  20 quiet-zone modules
  const barcodeModules = 11 * (barcode.length + 2) + 13 + 20;
  const barcodeDots    = barcodeModules * BAR_W;
  const barcodeX       = Math.max(10, Math.round((PW - barcodeDots) / 2));

  const parts = ['^XA', `^PW${PW}`, `^LL${LL}`, `^BY${BAR_W}`];

  if (label) {
    // ^FB<width>,<maxLines>,<lineSpacing>,<justification>  — 'C' = centred
    parts.push(`^FO0,25^FB${PW},1,0,C^A0N,45,45^FD${label}^FS`);
  }

  parts.push(
    `^FO${barcodeX},${label ? 85 : 70}^BCN,200,Y,N,N^FD${barcode}^FS`,
    `^PQ${qty}`,
    '^XZ',
  );
  const zpl = parts.join('');

  // Zebra Browser Print SDK hardcodes version:2 in the device object sent to /write.
  // Sending the raw version from the API (e.g. 5) causes ZBP to silently drop the job.
  const deviceForWrite = {
    name: printer.name,
    uid: printer.uid,
    connection: printer.connection,
    deviceType: printer.deviceType,
    version: 2,
    provider: printer.provider,
    manufacturer: printer.manufacturer,
  };
  const body = JSON.stringify({ device: deviceForWrite, data: zpl });
  console.log('[Zebra] POSTing to', `${BP}/write`, '| body:', body);

  const r = await fetch(`${BP}/write`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    signal: AbortSignal.timeout(10000),
  });

  const responseText = await r.text().catch(() => '');
  console.log('[Zebra] Response status:', r.status, '| body:', responseText);

  if (!r.ok) throw new Error(`Printer responded with ${r.status}${responseText ? ': ' + responseText : ''}`);
}

/**
 * Sends ~WC (print configuration label) to the printer.
 * This is a host command — no ZPL formatting needed — so if it produces
 * a label the ZBP→printer channel is confirmed working.
 * Use it as a diagnostic before blaming ZPL syntax.
 */
export async function testPrinterConnection(printer) {
  const deviceForWrite = {
    name: printer.name,
    uid: printer.uid,
    connection: printer.connection,
    deviceType: printer.deviceType,
    version: 2,
    provider: printer.provider,
    manufacturer: printer.manufacturer,
  };
  const body = JSON.stringify({ device: deviceForWrite, data: '~WC' });
  console.log('[Zebra] Sending ~WC (config label test) to', `${BP}/write`);

  const r = await fetch(`${BP}/write`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    signal: AbortSignal.timeout(10000),
  });
  const responseText = await r.text().catch(() => '');
  console.log('[Zebra] ~WC response:', r.status, responseText);
  if (!r.ok) throw new Error(`Test failed: ${r.status}${responseText ? ': ' + responseText : ''}`);
}
